import { useEffect, useRef } from 'react';

/**
 * Publishes the mounted bottom-row height for sticky consumers.
 *
 * ---
 * 🧠 **Context**
 *
 * - Attach the returned ref to the bottom-row container.
 * - The --bottomrow-height value intentionally persists after unmount because other page components use it.
 *
 * ---
 * @example
 * ```tsx
 * function BottomRow() {
 *   const bottomRowRef = useBottomRowHeight();
 *   return <div ref={bottomRowRef}>Categorias e cesta</div>;
 * }
 * ```
 *
 * ---
 * @function useBottomRowHeight
 * @returns {React.RefObject<HTMLDivElement>} Ref to attach to the bottom row.
 */
export function useBottomRowHeight() {
	const bottomRowRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (bottomRowRef.current) {
			const bottomRowHeight = bottomRowRef.current.getBoundingClientRect().height;

			document.documentElement.style.setProperty('--bottomrow-height', `${bottomRowHeight}px`);
		}
	}, []);

	return bottomRowRef;
}
